import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListRecitations, adminDecideRecitation, recitationAudioUrl, tokenStore, listModules, getModule } from "@/lib/apiClient";
import { Check, X, Clock, AlertCircle, Search, ChevronDown, ChevronUp } from "lucide-react";
import SectionContent from "@/pages/modules/reading/SectionContent";

// Read-only reproduction of the original lines from the module — NOT the
// learner Test page. No timer, no font controls, no recording controls.
// Reading modules reuse SectionContent, the exact same section-derivation +
// rendering TestSelfAssessment.jsx (the learner's own Test screen) uses, so
// the two can never show different content for the same section. Filtered
// to the recitation's own `passage_code` (section) when we have one. Older
// submissions made before that field existed have no passage_code, so we
// fall back to showing the whole module — the exact section can't be
// recovered after the fact.
function OriginalModuleContent({ module: m, passageCode }) {
  if (m.content_type === "mcq") {
    return (
      <div className="text-xs text-[#4B5563] italic">
        Original module content preview isn't available for quiz-based modules.
      </div>
    );
  }

  if (m.content_type === "recording") {
    const allAyahs = (m.passages || []).flatMap((p) => (p.ayahs || []).map((a) => ({ ...a, passage: p })));
    const ayahs = passageCode
      ? allAyahs.filter((a) => (a.rule || String(a.n)) === passageCode)
      : allAyahs;
    if (ayahs.length === 0) {
      return <div className="text-xs text-[#4B5563] italic">No module content found.</div>;
    }
    const isGroup = ayahs.length > 1;
    return (
      <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-3" data-testid="original-module-recording">
        {ayahs.map((a) => (
          <div key={a.n} className="rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] p-3">
            {isGroup && <div className="text-[10px] font-mono text-[#9CA3AF] mb-1.5" dir="ltr">Ayah {a.n}</div>}
            <div className="font-quran text-lg text-right leading-loose" dir="rtl">{a.ar}</div>
          </div>
        ))}
      </div>
    );
  }

  // "reading" (and any other item-based content type)
  return (
    <div className="max-h-[50vh] overflow-y-auto pr-1" data-testid="original-module-reading">
      <SectionContent module={m} section={passageCode} fontSizeClass="text-xl" groupFontSizeClass="text-xl" />
    </div>
  );
}

export default function AdminRecitations() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioUrls, setAudioUrls] = useState({}); // recitation_id -> object url
  const [busy, setBusy] = useState(null);
  const [modules, setModules] = useState([]);
  const [moduleCode, setModuleCode] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({}); // recitation_id -> bool
  const [moduleContent, setModuleContent] = useState({}); // module_code -> { loading, error, module }

  useEffect(() => { listModules().then(setModules).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListRecitations(status, { moduleCode: moduleCode || undefined, q: q || undefined });
      setRows(data);
      // Build auth'd blob URLs for the audio players
      const t = tokenStore.get();
      const urls = {};
      await Promise.all(data.map(async (r) => {
        try {
          const resp = await fetch(recitationAudioUrl(r.recitation_id), {
            headers: t ? { Authorization: `Bearer ${t}` } : {},
            credentials: "include",
          });
          if (resp.ok) urls[r.recitation_id] = URL.createObjectURL(await resp.blob());
        } catch (_e) { /* skip unavailable audio */ }
      }));
      setAudioUrls(urls);
    } finally { setLoading(false); }
  }, [status, moduleCode, q]);
  // Module/status filters refetch immediately; the free-text search box is
  // debounced so it doesn't re-fetch on every keystroke.
  useEffect(() => { load(); }, [status, moduleCode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setTimeout(load, 400);
    return () => clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loads the module content only on first expand of a given module code —
  // reuses the same GET /modules/{code} the student-facing pages already
  // call, so nothing here duplicates content into a new store. Subsequent
  // rows for the same module (or re-expanding the same row) reuse the cache.
  const toggleModuleView = async (r) => {
    const id = r.recitation_id;
    const willExpand = !expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: willExpand }));
    if (!willExpand || moduleContent[r.module_code]) return;
    setModuleContent((prev) => ({ ...prev, [r.module_code]: { loading: true } }));
    try {
      const data = await getModule(r.module_code);
      setModuleContent((prev) => ({ ...prev, [r.module_code]: { loading: false, module: data.module } }));
    } catch (_e) {
      setModuleContent((prev) => ({ ...prev, [r.module_code]: { loading: false, error: true } }));
    }
  };

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      await adminDecideRecitation(id, { decision });
      toast.success(decision === "approved" ? "Approved" : "Rejected");
      load();
    } catch { toast.error("Could not save decision"); }
    finally { setBusy(null); }
  };

  const TabBtn = ({ value, label }) => (
    <button data-testid={`tab-${value}`} onClick={() => setStatus(value)}
      className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
        status === value ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] text-[#1F2937] hover:border-[#064E3B]"
      }`}>{label}</button>
  );

  return (
    <div data-testid="admin-recitations">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">Recitations</h1>
      <div className="mt-5 flex flex-wrap gap-2">
        <TabBtn value="pending" label="Pending" />
        <TabBtn value="approved" label="Approved" />
        <TabBtn value="rejected" label="Rejected" />
        <TabBtn value="all" label="All" />
      </div>

      {/* Filters — narrow down to a specific module (and optionally a
          user) to quickly find and play back the most recent submission;
          the list is already sorted newest-first. */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="recitations-filters">
        <select data-testid="filter-module" value={moduleCode} onChange={(e) => setModuleCode(e.target.value)}
          className="px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm">
          <option value="">All modules</option>
          {modules.map((m) => <option key={m.code} value={m.code}>{m.n}. {m.name}</option>)}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by user name or email"
            className="pl-8 pr-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm w-64" />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <div className="text-[#4B5563]">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl bg-white border border-[#E7E0D2] p-10 text-center text-[#4B5563]">
            No recitations in this list.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.recitation_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-5" data-testid={`rec-${r.recitation_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-heading text-base text-[#1F2937]">{r.user_name}</div>
                <div className="text-xs text-[#4B5563]">{r.user_email} · {new Date(r.created_at).toLocaleString()}</div>
                {(r.module_code || r.module_name) && (
                  <button
                    data-testid={`toggle-module-view-${r.recitation_id}`}
                    onClick={() => toggleModuleView(r)}
                    title="View the original module content this recitation was submitted from"
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[#064E3B] hover:text-[#047857] underline decoration-dotted underline-offset-2 transition"
                  >
                    Module: {r.module_code === "recitation-fluency"
                      ? (r.passage_code ? `Final · ${r.passage_code}` : "Final · Module 9")
                      : (r.passage_code ? `${r.passage_code} - ${r.module_name || r.module_code}` : (r.module_name || r.module_code))}
                    {expanded[r.recitation_id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
                {r.ai_accuracy != null && (
                  <div className="mt-1 text-xs text-[#4B5563]">AI estimate: <b>{r.ai_accuracy}%</b></div>
                )}
              </div>
              <Status status={r.status} />
            </div>

            <div className="mt-3">
              {audioUrls[r.recitation_id] ? (
                <audio controls src={audioUrls[r.recitation_id]} className="w-full max-w-md" />
              ) : (
                <div className="text-xs text-[#4B5563]">Audio not available</div>
              )}
            </div>

            {expanded[r.recitation_id] && (
              <div className="mt-3 rounded-xl bg-[#FAF7F0] border border-[#E7E0D2] p-3" data-testid={`original-module-${r.recitation_id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
                  Original Module Content{r.passage_code ? "" : " · full module (section wasn't recorded for this submission)"}
                </div>
                {moduleContent[r.module_code]?.loading && <div className="text-xs text-[#4B5563]">Loading module content…</div>}
                {moduleContent[r.module_code]?.error && <div className="text-xs text-[#EF4444]">Could not load module content.</div>}
                {moduleContent[r.module_code]?.module && (
                  <OriginalModuleContent module={moduleContent[r.module_code].module} passageCode={r.passage_code} />
                )}
              </div>
            )}

            {r.status === "pending" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid={`approve-${r.recitation_id}`} onClick={() => decide(r.recitation_id, "approved")} disabled={busy === r.recitation_id}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white text-sm font-medium transition disabled:opacity-50">
                  <Check className="w-4 h-4" />
                  {r.module_code === "recitation-fluency" ? "Approve & issue certificate" : "Approve — mark module reviewed"}
                </button>
                <button data-testid={`reject-${r.recitation_id}`} onClick={() => decide(r.recitation_id, "rejected")} disabled={busy === r.recitation_id}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] text-sm font-medium transition disabled:opacity-50">
                  <X className="w-4 h-4" /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Status({ status }) {
  const cfg = {
    pending: { color: "#D97706", bg: "#FEF3C7", icon: Clock, label: "Pending" },
    approved: { color: "#065F46", bg: "#D1FAE5", icon: Check, label: "Approved" },
    rejected: { color: "#7F1D1D", bg: "#FEE2E2", icon: X, label: "Rejected" },
  }[status] || { color: "#4B5563", bg: "#F3F4F6", icon: AlertCircle, label: status };
  const I = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ color: cfg.color, background: cfg.bg }}>
      <I className="w-3 h-3" /> {cfg.label}
    </span>
  );
}
